"use client";

import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Section from "../components/Section";
import Card from "../components/Card";
import Button from "../components/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase } from "@fortawesome/free-solid-svg-icons/faBriefcase";
import { faScrewdriverWrench } from "@fortawesome/free-solid-svg-icons/faScrewdriverWrench";
import { faHandshake } from "@fortawesome/free-solid-svg-icons/faHandshake";
import { faBolt } from "@fortawesome/free-solid-svg-icons/faBolt";
import { faShieldHalved } from "@fortawesome/free-solid-svg-icons/faShieldHalved";
import { faCircleCheck } from "@fortawesome/free-solid-svg-icons/faCircleCheck";

/* ==== Simple email validation (no domain blacklist) ==== */
const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  return re.test(email);
};

/* ==== Inline Contact Form (sends via FormSubmit) ==== */
const InlineContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    message: "",
    phone: "",
  });
  const [touched, setTouched] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = "Full name is required.";
    if (!form.company.trim()) e.company = "Company is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!isValidEmail(form.email))
      e.email = "Enter a valid email address.";
    if (!form.message.trim()) e.message = "Tell us a bit about your needs.";
    return e;
  }, [form]);

  const hasErrors = Object.keys(errors).length > 0;

  const baseInput =
    "w-full rounded-2xl border-2 bg-white px-4 py-3 text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2";
  const validRing =
    "border-slate-200 focus:border-blue-400 focus:ring-blue-200";
  const invalidRing = "border-red-400 focus:border-red-500 focus:ring-red-200";

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const onBlur = (e) => setTouched((t) => ({ ...t, [e.target.name]: true }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      name: true,
      company: true,
      email: true,
      message: true,
      phone: true,
    });
    setSendError("");
    if (hasErrors) return;

    try {
      setIsSending(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      fd.append("_replyto", form.email);
      fd.append("_subject", "New Contact — Faef Global");
      fd.append("_template", "table");
      fd.append("_captcha", "false");
      fd.append("source", window.location.href);
      // Honeypot (bots only)
      fd.append("_honey", "");

      const res = await fetch(
        "https://formsubmit.co/ajax/info@faef.tech",
        {
          method: "POST",
          headers: { Accept: "application/json" },
          body: fd,
        }
      );

      if (!res.ok) throw new Error("Send failed");
      setSubmitted(true);
    } catch {
      setSendError(
        "We couldn’t send your message. Please try again, or email support@faef.tech."
      );
    } finally {
      setIsSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center space-y-4 py-8" aria-live="polite">
        <div className="mx-auto w-16 h-16 rounded-full grid place-items-center bg-green-100 ring-1 ring-green-200">
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="text-green-600 text-3xl"
            aria-hidden="true"
          />
        </div>
        <h4 className="text-2xl font-semibold text-gray-900">Message sent</h4>
        <p className="text-gray-600">We’ll get back to you shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-6">
      {/* hidden honeypot for bots */}
      <input
        type="text"
        name="_honey"
        className="hidden"
        tabIndex="-1"
        autoComplete="off"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <input
            name="name"
            value={form.name}
            onChange={onChange}
            onBlur={onBlur}
            className={`${baseInput} ${
              touched.name && errors.name ? invalidRing : validRing
            }`}
            placeholder="Your Full Name"
            required
          />
          {touched.name && errors.name && (
            <p className="mt-2 text-sm text-red-600">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Company
          </label>
          <input
            name="company"
            value={form.company}
            onChange={onChange}
            onBlur={onBlur}
            className={`${baseInput} ${
              touched.company && errors.company ? invalidRing : validRing
            }`}
            placeholder="Company Name"
            required
          />
          {touched.company && errors.company && (
            <p className="mt-2 text-sm text-red-600">{errors.company}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={onChange}
            onBlur={onBlur}
            className={`${baseInput} ${
              touched.email && errors.email ? invalidRing : validRing
            }`}
            placeholder="you@domain.com"
            required
          />
          {touched.email && errors.email && (
            <p className="mt-2 text-sm text-red-600">{errors.email}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone (optional)
          </label>
          <input
            name="phone"
            value={form.phone}
            onChange={onChange}
            onBlur={onBlur}
            className={`${baseInput} ${validRing}`}
            placeholder="+1 555 123 4567"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Message
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={onChange}
          onBlur={onBlur}
          rows={5}
          className={`${baseInput} ${
            touched.message && errors.message ? invalidRing : validRing
          }`}
          placeholder="Share your requirements, timelines, and any technical constraints…"
          required
        />
        {touched.message && errors.message && (
          <p className="mt-2 text-sm text-red-600">{errors.message}</p>
        )}
      </div>

      {sendError && (
        <p className="text-sm text-red-600" role="alert">
          {sendError}
        </p>
      )}

      <div className="pt-2">
        <Button
          type="submit"
          className={`text-lg px-10 py-4 rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600 text-white shadow-2xl shadow-blue-900/30 ring-1 ring-blue-500/10 transition-all duration-300 ${
            hasErrors || isSending ? "opacity-60 cursor-not-allowed" : ""
          }`}
          disabled={hasErrors || isSending}>
          {isSending ? "Sending…" : "Send Message"}
        </Button>
      </div>
    </form>
  );
};

const Contact = () => {
  const contactInfo = [
    // {
    //   title: "Enterprise Solutions",
    //   email: "enterprise@faef.tech",
    //   phone: "+1 (555) 123-4567",
    //   icon: faBriefcase,
    //   description: "Custom telecommunications solutions for global enterprises",
    // },
    {
      title: "Technical Operations",
      email: "support@faef.tech",
      phone: "+1 (555) 123-4568",
      icon: faScrewdriverWrench,
      description: "24/7 technical support and network operations center",
    },
    {
      title: "Strategic Partnerships",
      email: "partnerships@faef.tech",
      phone: "+1 (555) 123-4569",
      icon: faHandshake,
      description: "Partnership opportunities and strategic alliances",
    },
  ];

  const navigate = useNavigate();
  const location = useLocation();

  const scrollToAnchor = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - 0;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const goToContactStart = (e) => {
    if (location.pathname === "/contact") {
      e.preventDefault();
      if (location.hash !== "#contact-start")
        window.history.pushState(null, "", "#contact-start");
      requestAnimationFrame(() => scrollToAnchor("contact-start"));
    } else {
      navigate("/contact#contact-start");
    }
  };

  useEffect(() => {
    if (location.hash === "#contact-start") {
      const t = setTimeout(() => scrollToAnchor("contact-start"), 0);
      return () => clearTimeout(t);
    }
  }, [location]);

  const iconWrap = "bg-blue-600 text-white rounded-2xl p-3";
  const infoIcon = "text-white text-xl";

  return (
    <div className="animate-fade-in">
      {/* HERO */}
      <Section className="pt-28 lg:pt-32 pb-20 lg:pb-24 overflow-visible bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center max-w-6xl mx-auto space-y-12">
          <h1 className="text-6xl md:text-8xl font-bold text-gray-900 leading-[1.12]">
            Contact
            <span className="block text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text mt-4 pb-[0.22em] -mb-[0.22em]">
              Faef Global
            </span>
          </h1>
          <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed max-w-5xl mx-auto">
            Ready to revolutionize your telecommunications infrastructure? Our
            global team of experts is standing by to design the perfect solution
            for your enterprise needs
          </p>
        </div>
      </Section>

      <div id="contact-start" className="h-0 scroll-mt-24 md:scroll-mt-28" />

      {/* FORM + INFO */}
      <Section background="gray" className="py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-7xl mx-auto">
          {/* Contact Form */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Send us a Message
              </h2>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                Tell us about your telecommunications challenges and let our
                experts craft a solution that exceeds your expectations
              </p>
            </div>
            <Card className="p-6 sm:p-8" id="contact-form">
              <InlineContactForm />
            </Card>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                Get in Touch
              </h2>
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                Connect with our specialized teams for immediate assistance and
                expert consultation
              </p>
            </div>

            <div className="space-y-8">
              {contactInfo.map(({ title, email, phone, description, icon }) => (
                <Card
                  key={title}
                  className="p-6 sm:p-8 bg-white border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
                  <div className="flex items-start gap-5">
                    <div className={iconWrap} aria-hidden="true">
                      <FontAwesomeIcon icon={icon} className={infoIcon} />
                    </div>
                    <div className="space-y-4">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight break-words">
                        {title}
                      </h3>
                      <p className="text-lg text-gray-600 leading-relaxed">
                        {description}
                      </p>
                      <div className="space-y-3">
                        <p className="text-gray-600">
                          <a
                            href={`mailto:${email}`}
                            className="hover:text-blue-600 transition-colors font-medium text-lg break-words">
                            {email}
                          </a>
                        </p>
                        {phone && (
                          <p className="text-gray-600">
                            <a
                              href={`tel:${phone.replace(/[^\d+]/g, "")}`}
                              className="hover:text-blue-600 transition-colors font-medium text-lg break-words">
                              {phone}
                            </a>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* VALUES */}
      <Section className="py-32">
        <div className="text-center mb-20 space-y-12">
          <h2 className="text-5xl md:text-7xl font-bold text-gray-900 leading-[1.12]">
            Enterprise
            <span className="block text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text mt-6 pb-[0.18em] -mb-[0.18em]">
              Excellence
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Our commitment to delivering world-class telecommunications
            solutions with unmatched reliability and performance
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-7xl mx-auto">
          <Card className="text-center p-10 space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto">
              <FontAwesomeIcon
                icon={faBolt}
                className="text-blue-700 text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Ultra Performance
            </h3>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Sub-5ms latency with 99.99% uptime across our global network
              infrastructure
            </p>
          </Card>

          <Card className="text-center p-10 space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto">
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="text-blue-700 text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Quantum Security
            </h3>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Enterprise-grade encryption with quantum-safe protocols and
              zero-trust architecture
            </p>
          </Card>

          <Card className="text-center p-10 space-y-8 bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 transition-all duration-300">
            <div className="bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl w-20 h-20 flex items-center justify-center mx-auto">
              <FontAwesomeIcon
                icon={faHandshake}
                className="text-blue-700 text-4xl"
                aria-hidden="true"
              />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              Strategic Partnership
            </h3>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Dedicated enterprise support with proactive monitoring and
              strategic consulting
            </p>
          </Card>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-28 lg:py-32 relative overflow-visible bg-gradient-to-br from-blue-700 via-indigo-700 to-indigo-800 text-white">
        <div className="text-center space-y-12 max-w-5xl mx-auto relative z-10">
          <h2 className="text-5xl md:text-7xl font-bold leading-[1.12]">
            Your Next Move
            <span className="block text-blue-200 mt-6 pb-[0.18em] -mb-[0.18em]">
              Starts Here
            </span>
          </h2>
          <p className="text-2xl md:text-3xl text-blue-100 leading-relaxed">
            Talk to our enterprise team about building a carrier-grade
            communications backbone tailored to your growth.
          </p>
          <div className="pt-2">
            <Link
              to="/contact#contact-start"
              onClick={goToContactStart}
              className="inline-block text-xl md:text-2xl px-12 py-6 rounded-2xl
                         bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-600
                         text-white shadow-2xl shadow-blue-900/30 ring-1 ring-white/10 transition-all duration-300">
              Speak to an Expert
            </Link>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
